<template>
	<client-only>
		<AdaptiveContainer v-if="loading">
			<div class="block w-[1000vw] mt-[60px] mb-12">
				<div v-for="index in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
					<DjCardBlog :loading="true" :title="''" :category="''" :public-id="''" :cta="''" />
				</div>
			</div>
		</AdaptiveContainer>
		<carousel
			ref="carouselEl"
			v-if="showCarousel && !loading"
			:settings="settings"
			class="py-5"
			:class="`carousel-dj carousel-blog carousel-buttons-${buttonType}`"
		>
			<slide v-for="(item, index) in carouselData.concat(new Array(helperSlides))" :key="index">
				<div v-if="index > carouselData.length - 1"></div>
				<div v-else class="block w-full">
					<DjCardBlog
						@on-card-click="navigateToPost(item)"
						@on-category-click="navigateToCategory(item)"
						:category="localized(item.category?.name)"
						:public-id="item.image?.cloudinary || ''"
						:title="localized(item.name)"
						:cta="$t('blogPage.readMore')"
						:post-href="item.slug ? `/${locale}/blog/post/${item.slug}` : ''"
					/>
				</div>
			</slide>

			<template v-if="showCarouselAddons()" #addons="{ currentSlide, slidesCount }">
				<!-- Custom navigation -->
				<div class="carousel__navigation-buttons mx-auto">
					<button
						@click="slidePrev(currentSlide, containerSlideNumber)"
						class="carousel__prev transition-colors"
						:class="{ 'hide-prev': Math.ceil(currentSlide) < 1 }"
					></button>
					<button
						@click="
							slideTo(
								currentSlide + containerSlideNumber,
								slidesCount - helperSlides - containerSlideNumber
							)
						"
						class="carousel__next transition-colors"
						:class="{
							'hide-next': Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides,
						}"
					></button>
				</div>

				<!-- Custom pagination -->
				<div class="hidden sm:block carousel__custom-pagination mx-auto">
					<button
						v-for="(paginationEl, index) in Math.ceil((slidesCount - helperSlides) / containerSlideNumber)"
						:key="index"
						@click="
							slideTo(index * containerSlideNumber, slidesCount - helperSlides - containerSlideNumber)
						"
						class="h-4 w-4 m-2 bg-dj-gray-light rounded-full"
						:class="{
							'dot--active': activePagination(
								index,
								currentSlide,
								slidesCount,
								containerSlideNumber,
								helperSlides
							),
						}"
					></button>
				</div>
			</template>
		</carousel>
		<template #fallback>
			<AdaptiveContainer v-if="ssrStaticPosts.length > 0">
				<div class="block w-[1000vw] mt-[60px] mb-12">
					<div
						v-for="(item, index) in ssrStaticPosts"
						:key="item.id || item.slug || index"
						class="inline-block float-left w-[320px] mr-10"
					>
						<DjCardBlog
							:category="localized(item.category?.name)"
							:public-id="item.image?.cloudinary || ''"
							:title="localized(item.name)"
							:cta="$t('blogPage.readMore')"
							:post-href="item.slug ? `/${locale}/blog/post/${item.slug}` : ''"
						/>
					</div>
				</div>
			</AdaptiveContainer>
		</template>
	</client-only>
</template>

<script lang="ts">
import { ref, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { constructApiUrl } from "@@/composables/helpers";
// import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide, Pagination, Navigation } from "vue3-carousel";

type BlogPost = {
	id?: number;
	slug?: string;
	name?: Record<string, string>;
	category?: {
		id?: number;
		name?: Record<string, string>;
	};
	image?: {
		cloudinary?: string;
	};
	[key: string]: unknown;
};

export default defineComponent({
	name: "CarouselBlog",
	components: {
		Carousel,
		Slide,
		Pagination,
		Navigation,
	},
	props: {
		slideWidth: {
			type: Number,
			default: 320,
		},
		buttonType: {
			type: String,
			default: "white",
			validator: (value: string) => {
				// valid values
				return ["white", "gray"].includes(value);
			},
		},
		blogPosts: {
			type: Array,
			default: () => [],
		},
	},
	emits: ["loaded"],
	async setup(props, { emit }) {
		const { locale } = useI18n();
		const router = useRouter();
		const regionCode = useRegion.getCurrentRegion().code.toLowerCase();
		const blogData = ref<BlogPost[]>([]);
		const blogPostsCache = useState<BlogPost[]>(`carousel-blog-posts-${regionCode}`, () => []);
		const loading = ref(true);
		const showCarousel = ref(false);
		const carouselEl = ref<{ slideTo: (index: number) => void } | null>(null);
		let bodyWidth = getBodyWidth();
		const slideMargin = ref(20);
		const helperSlides = ref(0);
		const containerSlideNumber = ref(0);
		const mainRequestUrl = ref("");

		const { data: mainResponse, execute: executeMainRequest } = useFetch<{ data: BlogPost[] }>(
			() => mainRequestUrl.value,
			{
				server: true,
				immediate: false,
				default: () => ({ data: [] }),
			}
		);

		function getBodyWidth() {
			if (typeof document === "undefined") return 1280;
			return document.body?.offsetWidth || 1280;
		}

		function localized(value: unknown) {
			const localeMap = value as Record<string, string> | undefined;
			return localeMap?.[locale.value] || "";
		}

		const carouselData = computed(() => {
			if ((props.blogPosts as BlogPost[]).length > 0) {
				return [...(props.blogPosts as BlogPost[])];
			}
			return [...blogData.value];
		});

		const ssrStaticPosts = computed(() => {
			return carouselData.value.slice(0, 8);
		});

		async function getPosts() {
			if (blogPostsCache.value.length > 0) {
				blogData.value = [...blogPostsCache.value];
				loading.value = false;
				emit("loaded");
				return;
			}

			const url = constructApiUrl("/posts");
			url.searchParams.append("limit", "20");
			url.searchParams.append("region", useRegion.getCurrentRegion().code.toLowerCase());
			mainRequestUrl.value = url.href;
			await executeMainRequest();
			blogData.value = mainResponse.value?.data || [];
			blogPostsCache.value = [...blogData.value];
			loading.value = false;
			emit("loaded");
		}

		function navigateToPost(item: BlogPost) {
			if (item.slug) {
				router.push(`/${locale.value}/blog/post/${item.slug}`);
			}
		}

		function navigateToCategory(item: BlogPost) {
			if (item.category?.id) {
				router.push(`/${locale.value}/blog?category=${item.category.id}`);
			}
		}

		// Calculate slide numbers from elements width
		function getItemsNumber() {
			return bodyWidth / (getValidSlideWidth() + slideMargin.value);
		}

		function showCarouselAddons() {
			const containerWidth = getDjContainerWidth();
			return (
				(bodyWidth - (bodyWidth - containerWidth) / 2) / (getValidSlideWidth() + slideMargin.value) <
				carouselData.value.length
			);
		}

		function slideTo(i: number, maxIndex: number) {
			carouselEl.value?.slideTo(i <= maxIndex ? i : maxIndex);
		}

		function slidePrev(currentSlide: number, containerSlideNumber: number) {
			let goToIndex =
				currentSlide -
				(currentSlide % containerSlideNumber == 0 ? containerSlideNumber : currentSlide % containerSlideNumber);
			carouselEl.value?.slideTo(goToIndex);
		}

		function activePagination(
			index: number,
			currentSlide: number,
			slidesCount: number,
			containerSlideNumber: number,
			helperSlides: number
		) {
			const lastPagination = index == Math.ceil((slidesCount - helperSlides) / containerSlideNumber) - 1;
			if (lastPagination && Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides) {
				return true;
			} else if (
				Math.floor(currentSlide / containerSlideNumber) == index &&
				Math.ceil(currentSlide) < slidesCount - containerSlideNumber - helperSlides
			) {
				return true;
			}

			return false;
		}

		// Carousel settings
		let settings = {
			itemsToShow: 1,
			snapAlign: "start",
			mouseDrag: true,
			touchDrag: true,
			transition: 800,
		};

		function getValidSlideWidth() {
			const contWidth = getDjContainerWidth();
			return contWidth >= props.slideWidth ? props.slideWidth : contWidth - 20;
		}

		function calculateCarouselParams() {
			const containerWidth = getDjContainerWidth();
			containerSlideNumber.value = Math.floor(containerWidth / getValidSlideWidth());
			bodyWidth = getBodyWidth();
			slideMargin.value =
				containerSlideNumber.value > 1
					? (containerWidth - containerSlideNumber.value * getValidSlideWidth()) /
					  (containerSlideNumber.value - 1)
					: 20;
			settings.itemsToShow = getItemsNumber();
			helperSlides.value =
				Math.ceil((bodyWidth - containerWidth) / 2 / (getValidSlideWidth() + slideMargin.value)) +
				(bodyWidth > 1800 ? 1 : 0);
		}

		function isResized() {
			showCarousel.value = false;
			setTimeout(() => {
				// Recalculate carousel params
				calculateCarouselParams();
				showCarousel.value = true;
			}, 500);
		}

		let doResize: ReturnType<typeof setTimeout> | undefined = undefined;
		function handleResize() {
			if (doResize) clearTimeout(doResize);
			// Resize ONLY width change (mobile hiding navbar handling)
			bodyWidth != getBodyWidth() ? (doResize = setTimeout(isResized, 500)) : null;
		}

		onMounted(() => {
			calculateCarouselParams();
			showCarousel.value = carouselData.value.length > 0;
			window.addEventListener("resize", handleResize);
		});

		if ((props.blogPosts as BlogPost[]).length > 0) {
			blogData.value = [...(props.blogPosts as BlogPost[])];
			loading.value = false;
			emit("loaded");
		} else {
			await getPosts();
		}

		return {
			carouselEl,
			settings,
			slideMargin,
			showCarousel,
			helperSlides,
			showCarouselAddons,
			containerSlideNumber,
			slidePrev,
			slideTo,
			activePagination,
			locale,
			loading,
			carouselData,
			ssrStaticPosts,
			localized,
			navigateToPost,
			navigateToCategory,
		};
	},
});
</script>
<!-- <style> not working on prod -->
<!--
<style lang="scss">
@import "~/assets/css/carousel.scss";

body {
	min-height: calc(100vh + 1px);
}

.carousel-blog {
	.carousel {
		&__track {
			@media (min-width: theme("screens.sm")) {
				left: calc((100vw - theme("maxWidth.dj-sm")) / 2);
			}
		}

		&__slide {
			justify-content: flex-start;
			align-items: flex-start;
			padding-right: 20px;

			@media (min-width: theme("screens.sm")) {
				padding-right: 40px;
			}
		}
	}
}
</style>
-->
